/**
 * FiMarginTrendChart — gross profit ($) bars against margin (%) line,
 * with a 40% reference line marking the target margin.
 */
import { useMemo } from 'react'
import {
  Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer, ComposedChart,
} from 'recharts'
import type { FiPeriod } from './fiTypes'
import { formatCurrency, formatCompact, formatPct } from './fiFormat'

const TARGET_MARGIN_PCT = 40

interface FiMarginTrendChartProps {
  trend: FiPeriod[]
}

interface ChartRow {
  period: string
  grossProfit: number
  marginPct: number | null
}

interface TooltipPayloadEntry {
  value?: number | null
  dataKey?: string
}

function ChartTooltip({ active, payload, label }: {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string
}) {
  if (!active || !payload || payload.length === 0) return null
  const profit = payload.find(p => p.dataKey === 'grossProfit')?.value ?? 0
  const margin = payload.find(p => p.dataKey === 'marginPct')?.value ?? null
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3 min-w-[180px]">
      <p className="font-semibold text-white mb-2">{label}</p>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-gray-300">Utilidad Bruta</span>
          <span className="text-white font-medium">{formatCurrency(profit as number)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-300">Margen</span>
          <span className="text-cyan-400 font-medium">{formatPct(margin as number | null)}</span>
        </div>
      </div>
    </div>
  )
}

export default function FiMarginTrendChart({ trend }: FiMarginTrendChartProps) {
  const data: ChartRow[] = useMemo(() => trend.map(p => ({
    period: p.label,
    grossProfit: p.grossProfit,
    marginPct: p.marginPct,
  })), [trend])

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Tendencia de Margen</h3>
        <p className="text-gray-400 text-sm">Utilidad bruta y margen % vs objetivo ({TARGET_MARGIN_PCT}%)</p>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Sin datos suficientes.</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
              <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis yAxisId="profit" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickFormatter={v => formatCompact(v)} />
              <YAxis yAxisId="margin" orientation="right" axisLine={false} tickLine={false}
                tick={{ fontSize: 11, fill: '#22d3ee' }} tickFormatter={v => `${v}%`} domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="square" iconSize={10}
                formatter={(value: string) => (
                  <span className="text-gray-400 text-xs">
                    {value === 'grossProfit' ? 'Utilidad Bruta' : value === 'marginPct' ? 'Margen %' : value}
                  </span>
                )}
              />
              <ReferenceLine yAxisId="margin" y={TARGET_MARGIN_PCT} stroke="#f59e0b" strokeDasharray="4 4"
                label={{ value: `${TARGET_MARGIN_PCT}%`, position: 'right', fill: '#f59e0b', fontSize: 11 }} />
              <Bar yAxisId="profit" dataKey="grossProfit" fill="#10b981" radius={[4, 4, 0, 0]} name="grossProfit" />
              <Line yAxisId="margin" type="monotone" dataKey="marginPct" stroke="#22d3ee" strokeWidth={2}
                dot={{ r: 3 }} name="marginPct" connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
